import { useState } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import httpClient from "api/httpClient";

export default function AgentRedeem() {
  const [query, setQuery] = useState("");
  const [customer, setCustomer] = useState(null);
  const [wallet, setWallet] = useState(null);

  const [points, setPoints] = useState("");
  const [pin, setPin] = useState("");

  const [loading, setLoading] = useState(false);

  const [snack, setSnack] = useState({ open: false, color: "success", content: "" });
  const openSnack = (color, content) => setSnack({ open: true, color, content });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const findCustomer = async () => {
    setLoading(true);
    try {
      const res = await httpClient.get(`/customers/resolve?q=${encodeURIComponent(query)}`);
      const first = res.data?.content;

      if (!first) {
        setCustomer(null);
        setWallet(null);
        openSnack("warning", "No customer found. Try card number or phone.");
        return;
      }

      setCustomer(first);

      const w = await httpClient.get(`/customers/${first.id}/wallet`);
      setWallet(w.data);
    } catch (e) {
      openSnack("error", e?.response?.data?.message || e.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const validateRedeem = () => {
    if (!customer) {
      openSnack("warning", "Search/select a customer first");
      return false;
    }

    const pts = Number(points);
    if (!points || Number.isNaN(pts) || pts <= 0) {
      openSnack("warning", "Enter valid points");
      return false;
    }

    const p = (pin || "").trim();
    if (!p) {
      openSnack("warning", "Customer PIN is required");
      return false;
    }
    if (!/^\d+$/.test(p)) {
      openSnack("warning", "PIN must be numeric");
      return false;
    }
    if (p.length < 4 || p.length > 6) {
      openSnack("warning", "PIN must be 4–6 digits");
      return false;
    }

    return true;
  };

  const redeem = async () => {
    if (!validateRedeem()) return;

    setLoading(true);
    try {
      const res = await httpClient.post("/transactions/redeem", {
        customer_id: customer.id,
        points: Number(points),
        pin: pin.trim(),
      });

      openSnack("success", `Redeemed. Tx: ${res.data?.internal_ref || "OK"}`);

      const w = await httpClient.get(`/customers/${customer.id}/wallet`);
      setWallet(w.data);

      // reset inputs after success
      setPoints("");
      setPin("");
    } catch (e) {
      openSnack("error", e?.response?.data?.message || e.message || "Redeem failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <MDTypography variant="h4" mb={2}>
          Agent Redeem
        </MDTypography>

        <Card>
          <MDBox p={2} display="flex" gap={2} alignItems="center">
            <MDInput
              label="Search customer (card / phone / patient)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              fullWidth
              onKeyUp={(e) => e.key === "Enter" && findCustomer()}
            />
            <MDButton color="info" onClick={findCustomer} disabled={loading}>
              <Icon>search</Icon>&nbsp;Find
            </MDButton>
          </MDBox>

          {customer ? (
            <MDBox p={2} pt={0}>
              <MDTypography variant="button" fontWeight="bold">
                {customer.card_number} — {customer.owner_name || customer.client_name || "Customer"}
              </MDTypography>

              <MDBox alignItems="left" display="flex" gap={1} fontSize="0.75rem">
                Available:
                <MDTypography
                  variant="caption"
                  display="block"
                  fontWeight="bold"
                  fontSize="1.0rem"
                  color="success"
                >
                  {Number(wallet?.available_points || 0).toLocaleString()}
                </MDTypography>
                | Pending:
                <MDTypography
                  variant="caption"
                  display="block"
                  fontWeight="bold"
                  fontSize="1.0rem"
                  color="warning"
                >
                  {Number(wallet?.pending_points || 0).toLocaleString()}
                </MDTypography>
              </MDBox>
            </MDBox>
          ) : null}

          <MDBox p={2} pt={0} display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <MDInput
              label="Points to redeem"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              sx={{ maxWidth: 240 }}
              inputProps={{ inputMode: "numeric" }}
            />
            <MDInput
              label="Customer PIN"
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              sx={{ maxWidth: 240 }}
              inputProps={{ inputMode: "numeric", maxLength: 6 }}
            />
            <MDButton color="error" onClick={redeem} disabled={loading}>
              <Icon>payment</Icon>&nbsp;Redeem
            </MDButton>
          </MDBox>
        </Card>
      </MDBox>

      <MDSnackbar
        color={snack.color}
        icon="notifications"
        title="Loyalty System"
        content={snack.content}
        open={snack.open}
        onClose={closeSnack}
        close={closeSnack}
        bgWhite
      />
    </DashboardLayout>
  );
}
